/**
 * Request deduplication utility to prevent duplicate API calls
 * Especially useful for form submissions and user creation
 */

const pendingRequests = new Map<string, Promise<any>>();

/**
 * Deduplicate requests by key. If a request with the same key is already pending,
 * return the existing promise instead of making a new request.
 * 
 * @param key - Unique identifier for the request ('create-user-email@example.com')
 * @param requestFn - Function that returns a promise for the actual request
 * @param ttl - Time to live in milliseconds (default: 30 seconds)
 * @returns Promise that resolves with the request result
 */
export async function deduplicateRequest<T>(
  key: string,
  requestFn: () => Promise<T>,
  ttl: number = 30000
): Promise<T> {
  // Check if we already have a pending request for this key
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key) as Promise<T>;
  }

  // Create the request promise
  const requestPromise = requestFn();

  // Store it in our pending requests map
  pendingRequests.set(key, requestPromise);

  // Set up cleanup after TTL
  const cleanup = () => {
    pendingRequests.delete(key);
  };
  
  // Clean up after the request completes (success or failure)
  requestPromise
    .then(() => cleanup())
    .catch(() => cleanup());
  
  // Also clean up after TTL in case the request hangs
  setTimeout(cleanup, ttl);
  
  return requestPromise;
}

/**
 * Create a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | undefined;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  };
}

/**
 * Clear all pending requests (useful for testing or manual cleanup)
 */
export function clearAllPendingRequests(): void {
  pendingRequests.clear();
}

/**
 * Get count of pending requests (useful for debugging)
 */
export function getPendingRequestCount(): number {
  return pendingRequests.size;
}